import React from "react";

/* ---------------------------------------------------------------- */
/* Card                                                              */
/* ---------------------------------------------------------------- */
export interface CardProps {
  title?: string;
  titleRight?: React.ReactNode;
  children: React.ReactNode;
  style?: React.CSSProperties;
}
export function Card({ title, titleRight, children, style }: CardProps) {
  return (
    <div className="netiv-card" style={style}>
      {title && (
        <div className="netiv-card__title">
          <span>{title}</span>
          {titleRight}
        </div>
      )}
      {children}
    </div>
  );
}

/* ---------------------------------------------------------------- */
/* KPI grid                                                          */
/* ---------------------------------------------------------------- */
export interface KpiItem {
  label: string;
  value: React.ReactNode;
  sub?: React.ReactNode;
  tone?: "up" | "down" | "warn" | "gold" | "dao" | "neutral";
}
const toneColor: Record<string, string> = {
  up: "var(--netiv-up)",
  down: "var(--netiv-down)",
  warn: "var(--netiv-warn)",
  gold: "var(--netiv-gold)",
  dao: "var(--netiv-dao)",
  neutral: "var(--netiv-text)",
};
export function KpiGrid({ items }: { items: KpiItem[] }) {
  return (
    <div className="netiv-kpi-grid">
      {items.map((it, i) => (
        <div className="netiv-kpi" key={i}>
          <div className="netiv-kpi__label">{it.label}</div>
          <div className="netiv-kpi__value" style={{ color: toneColor[it.tone ?? "neutral"] }}>
            {it.value}
          </div>
          {it.sub && <div className="netiv-kpi__sub">{it.sub}</div>}
        </div>
      ))}
    </div>
  );
}

/* ---------------------------------------------------------------- */
/* Hero — dark headline metric panel (e.g. WSI value)                */
/* ---------------------------------------------------------------- */
export interface HeroProps {
  eyebrow: string;
  value: React.ReactNode;
  chips?: React.ReactNode[];
}
export function Hero({ eyebrow, value, chips }: HeroProps) {
  return (
    <div className="netiv-hero">
      <div className="netiv-hero__eyebrow">{eyebrow}</div>
      <div className="netiv-hero__value">{value}</div>
      {chips && chips.length > 0 && (
        <div className="netiv-hero__row">
          {chips.map((c, i) => (
            <React.Fragment key={i}>{c}</React.Fragment>
          ))}
        </div>
      )}
    </div>
  );
}
export function HeroChange({ children, tone = "up" }: { children: React.ReactNode; tone?: "up" | "down" }) {
  return (
    <span style={{ fontSize: 14, fontWeight: 600, color: tone === "up" ? "var(--netiv-up)" : "var(--netiv-down)" }}>
      {children}
    </span>
  );
}
export function HeroChip({ children }: { children: React.ReactNode }) {
  return <span className="netiv-hero__chip">{children}</span>;
}

/* ---------------------------------------------------------------- */
/* Badge                                                             */
/* ---------------------------------------------------------------- */
export type BadgeTone = "up" | "down" | "warn" | "blue" | "purple" | "dao" | "neutral";
export function Badge({ tone = "neutral", children }: { tone?: BadgeTone; children: React.ReactNode }) {
  return <span className={`netiv-badge netiv-badge--${tone}`}>{children}</span>;
}

/* ---------------------------------------------------------------- */
/* Chips (filter pills)                                              */
/* ---------------------------------------------------------------- */
export function Chips({
  options,
  value,
  onChange,
}: {
  options: string[];
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <div className="netiv-chips">
      {options.map((o) => (
        <span
          key={o}
          className={`netiv-chip ${o === value ? "netiv-chip--on" : ""}`}
          onClick={() => onChange(o)}
        >
          {o}
        </span>
      ))}
    </div>
  );
}

/* ---------------------------------------------------------------- */
/* Section row                                                       */
/* ---------------------------------------------------------------- */
export function SectionRow({ children, linkLabel, onLink }: { children: React.ReactNode; linkLabel?: string; onLink?: () => void }) {
  return (
    <div className="netiv-section-row">
      <span>{children}</span>
      {linkLabel && <span className="netiv-section-row__link" onClick={onLink}>{linkLabel}</span>}
    </div>
  );
}

/* ---------------------------------------------------------------- */
/* Signal list                                                       */
/* ---------------------------------------------------------------- */
export interface SignalItem {
  color: string;
  title: string;
  desc: string;
  meta?: string;
}
export function SignalList({ items }: { items: SignalItem[] }) {
  return (
    <Card>
      {items.map((s, i) => (
        <div className="netiv-signal" key={i}>
          <div className="netiv-signal__dot" style={{ background: s.color }} />
          <div>
            <div className="netiv-signal__title">{s.title}</div>
            <div className="netiv-signal__desc">{s.desc}</div>
            {s.meta && <div className="netiv-signal__meta">{s.meta}</div>}
          </div>
        </div>
      ))}
    </Card>
  );
}

/* ---------------------------------------------------------------- */
/* Data table                                                        */
/* ---------------------------------------------------------------- */
export function DataTable({
  columns,
  rows,
  onRowClick,
}: {
  columns: string[];
  rows: React.ReactNode[][];
  onRowClick?: (rowIndex: number) => void;
}) {
  return (
    <table className="netiv-table">
      <thead>
        <tr>
          {columns.map((c) => (
            <th key={c}>{c}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((r, i) => (
          <tr
            key={i}
            className={onRowClick ? "netiv-table__row--interactive" : undefined}
            onClick={() => onRowClick?.(i)}
            tabIndex={onRowClick ? 0 : undefined}
            onKeyDown={(event) => {
              if (onRowClick && (event.key === "Enter" || event.key === " ")) {
                event.preventDefault();
                onRowClick(i);
              }
            }}
          >
            {r.map((cell, j) => (
              <td key={j}>{cell}</td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/* ---------------------------------------------------------------- */
/* Vote / progress bar                                               */
/* ---------------------------------------------------------------- */
export interface VoteBarSeg {
  pct: number;
  color: string;
  label?: string;
}
export function VoteBar({ segments }: { segments: VoteBarSeg[] }) {
  return (
    <div className="netiv-bar">
      {segments.map((s, i) => (
        <div
          key={i}
          className="netiv-bar__seg"
          style={{ width: `${s.pct}%`, background: s.color, minWidth: s.label ? 26 : 3 }}
        >
          {s.label}
        </div>
      ))}
    </div>
  );
}

/* ---------------------------------------------------------------- */
/* Provenance step row                                               */
/* ---------------------------------------------------------------- */
export function ProvenanceStep({ index, title, sub, right }: { index: number; title: string; sub: string; right?: React.ReactNode }) {
  return (
    <div className="netiv-prow">
      <div className="netiv-prow__num">{index}</div>
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 13, fontWeight: 600 }}>{title}</div>
        <div style={{ fontSize: 11, color: "var(--netiv-text-2)" }}>{sub}</div>
      </div>
      {right}
    </div>
  );
}

/* ---------------------------------------------------------------- */
/* Chart container helper                                            */
/* ---------------------------------------------------------------- */
export function ChartBox({ size = "md", children }: { size?: "sm" | "md" | "lg"; children: React.ReactNode }) {
  return <div className={`netiv-chart netiv-chart--${size}`}>{children}</div>;
}
